import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Phone, Plus, X, Pencil, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { leadApi } from '@/lib/api';
import { ClickToCall } from '@/components/ClickToCall';
import LastPickupBadge, { pickupForPhone } from '@/components/LastPickupBadge';

/**
 * Editable stack of "additional numbers" (3rd, 4th, … contact numbers
 * beyond the primary + secondary).  Each entry has click-to-call, an
 * inline edit pencil, and a remove (X) button.  An `+ Add another`
 * chip at the bottom opens a new input row.
 *
 * Persisted to `leads.additional_numbers` (string array) via
 * PUT /api/leads/{id}.  Lives in its own dedicated field, completely
 * separate from `notes` — agents adding notes can never overwrite or
 * bury these numbers.
 */
export default function AdditionalNumbersField({ leadId, value = [], leadName, onRefresh, byPickup = {}, winnerPhone = null }) {
    const [numbers, setNumbers] = useState(Array.isArray(value) ? value : []);
    const [editingIdx, setEditingIdx] = useState(null);
    const [draft, setDraft] = useState('');
    const [adding, setAdding] = useState(false);
    const [newDraft, setNewDraft] = useState('');
    const [savingIdx, setSavingIdx] = useState(null);

    useEffect(() => {
        setNumbers(Array.isArray(value) ? value : []);
        setEditingIdx(null);
        setAdding(false);
    }, [leadId, JSON.stringify(value)]);

    const validate = (raw) => {
        const trimmed = (raw || '').trim();
        if (!trimmed) return { ok: false, msg: 'Number is empty' };
        if (trimmed.replace(/\D/g, '').length < 7) return { ok: false, msg: 'Phone must be at least 7 digits' };
        return { ok: true, value: trimmed };
    };

    const persist = async (nextList, opIndex = null) => {
        setSavingIdx(opIndex);
        try {
            await leadApi.update(leadId, { additional_numbers: nextList });
            setNumbers(nextList);
            onRefresh?.(nextList);
            return true;
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save additional numbers');
            return false;
        } finally {
            setSavingIdx(null);
        }
    };

    const handleAdd = async (e) => {
        e?.preventDefault?.();
        const v = validate(newDraft);
        if (!v.ok) { toast.error(v.msg); return; }
        if (numbers.some(n => (n || '').replace(/\D/g, '') === v.value.replace(/\D/g, ''))) {
            toast.error('This number is already in the list');
            return;
        }
        const ok = await persist([...numbers, v.value], -1);
        if (ok) {
            toast.success('Number added');
            setNewDraft('');
            setAdding(false);
        }
    };

    const handleUpdate = async (idx) => {
        const v = validate(draft);
        if (!v.ok) { toast.error(v.msg); return; }
        const next = [...numbers];
        next[idx] = v.value;
        const ok = await persist(next, idx);
        if (ok) {
            toast.success('Number updated');
            setEditingIdx(null);
            setDraft('');
        }
    };

    const handleRemove = async (idx) => {
        const next = numbers.filter((_, i) => i !== idx);
        const ok = await persist(next, idx);
        if (ok) toast.success('Number removed');
    };

    return (
        <div
            className="mt-3 rounded-md border border-orange-200 bg-orange-50/60 px-3 py-2"
            data-testid={`lead-detail-additional-editor-${leadId}`}
        >
            <div className="text-[11px] font-semibold uppercase tracking-wide text-orange-700 mb-1.5 inline-flex items-center gap-1.5">
                <Phone className="h-3 w-3" /> Additional Numbers
                <span className="ml-1 text-[9px] font-normal normal-case text-muted-foreground">
                    Saved separately — notes won't touch these.
                </span>
            </div>

            <div className="space-y-1.5">
                {numbers.length === 0 && !adding && (
                    <div className="text-[11px] text-muted-foreground italic">
                        No additional numbers yet.
                    </div>
                )}

                {numbers.map((n, idx) => (
                    <div
                        key={`${n}-${idx}`}
                        className="flex items-center gap-2 bg-white rounded border border-orange-100 px-2 py-1"
                        data-testid={`lead-additional-number-${idx}`}
                    >
                        {editingIdx === idx ? (
                            <>
                                <Input
                                    type="tel"
                                    value={draft}
                                    onChange={(e) => setDraft(e.target.value)}
                                    className="h-7 text-sm font-mono flex-1"
                                    autoFocus
                                    data-testid={`lead-additional-edit-input-${idx}`}
                                />
                                <Button
                                    size="sm"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => handleUpdate(idx)}
                                    disabled={savingIdx === idx}
                                    data-testid={`lead-additional-edit-save-${idx}`}
                                >
                                    {savingIdx === idx ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2 text-xs"
                                    onClick={() => { setEditingIdx(null); setDraft(''); }}
                                    disabled={savingIdx === idx}
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </>
                        ) : (
                            <>
                                <span className="text-[9px] font-bold uppercase tracking-wide text-orange-700 bg-orange-100 rounded px-1 py-0.5">
                                    {idx === 0 ? '3rd' : idx === 1 ? '4th' : `#${idx + 3}`}
                                </span>
                                <span className="font-mono text-xs flex-1 truncate">{n}</span>
                                {(() => {
                                    const p = pickupForPhone(byPickup, n);
                                    if (!p) return null;
                                    const isWin = winnerPhone && pickupForPhone({ [winnerPhone]: true }, n) === true;
                                    return <LastPickupBadge pickup={p} isWinner={isWin} compact />;
                                })()}
                                <ClickToCall
                                    phoneNumber={n}
                                    contactId={leadId}
                                    contactName={leadName}
                                    size="sm"
                                    className="h-6 w-6"
                                />
                                <button
                                    type="button"
                                    onClick={() => { setEditingIdx(idx); setDraft(n); }}
                                    className="h-6 w-6 inline-flex items-center justify-center rounded text-orange-600 hover:bg-orange-100"
                                    title="Edit"
                                    data-testid={`lead-additional-edit-btn-${idx}`}
                                >
                                    <Pencil className="h-3 w-3" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleRemove(idx)}
                                    disabled={savingIdx === idx}
                                    className="h-6 w-6 inline-flex items-center justify-center rounded text-red-600 hover:bg-red-50 disabled:opacity-50"
                                    title="Remove"
                                    data-testid={`lead-additional-remove-btn-${idx}`}
                                >
                                    {savingIdx === idx ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                                </button>
                            </>
                        )}
                    </div>
                ))}

                {adding ? (
                    <form
                        onSubmit={handleAdd}
                        className="flex items-center gap-2 bg-white rounded border border-orange-300 px-2 py-1"
                    >
                        <Input
                            type="tel"
                            value={newDraft}
                            onChange={(e) => setNewDraft(e.target.value)}
                            placeholder="+9715XXXXXXXX"
                            className="h-7 text-sm font-mono flex-1"
                            autoFocus
                            data-testid={`lead-additional-new-input-${leadId}`}
                        />
                        <Button
                            type="submit"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            disabled={savingIdx === -1}
                            data-testid={`lead-additional-new-save-${leadId}`}
                        >
                            {savingIdx === -1 ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            disabled={savingIdx === -1}
                            onClick={() => { setAdding(false); setNewDraft(''); }}
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    </form>
                ) : (
                    <button
                        type="button"
                        onClick={() => setAdding(true)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 mt-1 rounded-md text-[11px] font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 border border-dashed border-orange-300 transition-colors"
                        data-testid={`lead-additional-add-btn-${leadId}`}
                    >
                        <Plus className="h-3 w-3" />
                        Add another number
                    </button>
                )}
            </div>
        </div>
    );
}
